import React, { useState, useMemo } from 'react';
import {
  Users,
  GraduationCap,
  AlertCircle,
  Clock,
  Building2,
  ChevronDown,
  ChevronUp,
  UserMinus,
  Workflow,
  MessageSquare,
  Calendar,
  Target
} from 'lucide-react';

interface TrainingRequirement {
  role: string;
  training_type: 'basic' | 'intermediate' | 'advanced';
  estimated_hours: number;
  delivery_method: 'self-paced' | 'instructor-led' | 'hands-on';
}

interface ChangeImpactData {
  roles_affected: number;
  departments_affected: string[];
  training_level: 'Low' | 'Medium' | 'High';
  training_requirements: TrainingRequirement[];
  estimated_training_hours: number;
  communication_triggers: string[];
  rollout_recommendation: 'big_bang' | 'phased' | 'pilot';
  change_velocity: 'slow' | 'moderate' | 'fast';
  support_requirements: {
    tier1_volume_increase: string;
    documentation_updates: number;
    faq_items_needed: number;
  };
}

interface ChangeImpactAssessmentProps {
  opportunity: {
    id: number;
    title: string;
    affected_steps?: any[];
    compliance_requirements?: any;
    implementation_complexity?: string;
    change_impact_data?: ChangeImpactData;
  };
  isExpanded?: boolean;
}

const ChangeImpactAssessment: React.FC<ChangeImpactAssessmentProps> = ({
  opportunity,
  isExpanded: initialExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  const impactData = useMemo((): ChangeImpactData => {
    if (opportunity.change_impact_data) {
      return opportunity.change_impact_data;
    }

    const affectedSteps = opportunity.affected_steps || [];
    const changeManagement = opportunity.compliance_requirements?.change_management;
    const complexity = opportunity.implementation_complexity || 'medium';

    const departments = new Set<string>();
    const roles = new Set<string>();

    affectedSteps.forEach((step: any) => {
      if (step.department) departments.add(step.department);
      if (step.owner) roles.add(step.owner);
      if (step.role) roles.add(step.role);
    });

    const rolesAffected = Math.max(roles.size, affectedSteps.length * 2);

    let trainingLevel: 'Low' | 'Medium' | 'High' = 'Low';
    if (complexity === 'high' || rolesAffected > 20) {
      trainingLevel = 'High';
    } else if (complexity === 'medium' || rolesAffected > 10) {
      trainingLevel = 'Medium';
    }

    const trainingRequirements: TrainingRequirement[] = [];

    if (changeManagement?.trainingRequirements?.requiredRoles) {
      changeManagement.trainingRequirements.requiredRoles.forEach((role: string) => {
        trainingRequirements.push({
          role,
          training_type: trainingLevel === 'High' ? 'advanced' : trainingLevel === 'Medium' ? 'intermediate' : 'basic',
          estimated_hours: trainingLevel === 'High' ? 8 : trainingLevel === 'Medium' ? 4 : 2,
          delivery_method: trainingLevel === 'High' ? 'instructor-led' : 'self-paced'
        });
      });
    } else {
      trainingRequirements.push({
        role: 'End Users',
        training_type: 'basic',
        estimated_hours: 2,
        delivery_method: 'self-paced'
      });

      if (trainingLevel !== 'Low') {
        trainingRequirements.push({
          role: 'Power Users / Champions',
          training_type: 'intermediate',
          estimated_hours: 4,
          delivery_method: 'instructor-led'
        });
      }

      if (trainingLevel === 'High') {
        trainingRequirements.push({
          role: 'System Administrators',
          training_type: 'advanced',
          estimated_hours: 8,
          delivery_method: 'hands-on'
        });
      }
    }

    const totalTrainingHours = trainingRequirements.reduce((sum, t) => sum + t.estimated_hours, 0) * rolesAffected;

    const communicationTriggers = [
      'Initial announcement to all stakeholders',
      'Training schedule notification (1 week before)',
      'Go-live reminder (2 days before)',
      'Post-implementation feedback survey'
    ];

    if (departments.size > 2) {
      communicationTriggers.push('Cross-department alignment meeting');
    }

    if (rolesAffected > 50) {
      communicationTriggers.push('Town hall / All-hands presentation');
    }

    let rolloutRecommendation: 'big_bang' | 'phased' | 'pilot' = 'big_bang';
    if (complexity === 'high' || rolesAffected > 50) {
      rolloutRecommendation = 'phased';
    } else if (complexity === 'medium' || rolesAffected > 20) {
      rolloutRecommendation = 'pilot';
    }

    let changeVelocity: 'slow' | 'moderate' | 'fast' = 'fast';
    if (complexity === 'high') {
      changeVelocity = 'slow';
    } else if (complexity === 'medium') {
      changeVelocity = 'moderate';
    }

    return {
      roles_affected: rolesAffected,
      departments_affected: Array.from(departments),
      training_level: trainingLevel,
      training_requirements: trainingRequirements,
      estimated_training_hours: totalTrainingHours,
      communication_triggers: communicationTriggers,
      rollout_recommendation: rolloutRecommendation,
      change_velocity: changeVelocity,
      support_requirements: {
        tier1_volume_increase: complexity === 'high' ? '+40%' : complexity === 'medium' ? '+20%' : '+10%',
        documentation_updates: affectedSteps.length * 2,
        faq_items_needed: Math.ceil(affectedSteps.length * 1.5)
      }
    };
  }, [opportunity]);

  const getTrainingLevelColor = (level: string) => {
    switch (level) {
      case 'High': return 'bg-red-100 text-red-800 border-red-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-green-100 text-green-800 border-green-300';
    }
  };

  const getRolloutIcon = (type: string) => {
    switch (type) {
      case 'phased': return '📊';
      case 'pilot': return '🧪';
      default: return '🚀';
    }
  };

  const getRolloutLabel = (type: string) => {
    switch (type) {
      case 'phased': return 'Phased Rollout';
      case 'pilot': return 'Pilot First';
      default: return 'Big Bang';
    }
  };

  const getVelocityColor = (velocity: string) => {
    switch (velocity) {
      case 'slow': return 'text-red-600';
      case 'moderate': return 'text-yellow-600';
      default: return 'text-green-600';
    }
  };

  return (
    <div className="border border-teal-200 rounded-lg bg-gradient-to-br from-teal-50 to-cyan-50">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-teal-100/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-teal-600" />
          <span className="font-semibold text-teal-900">Change Impact Assessment</span>
          <div className="flex gap-2 ml-2">
            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded flex items-center gap-1">
              <Users className="w-3 h-3" />
              {impactData.roles_affected} Roles
            </span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getTrainingLevelColor(impactData.training_level)}`}>
              <GraduationCap className="w-3 h-3 inline mr-1" />
              {impactData.training_level} Training
            </span>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-teal-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-teal-600" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
              <div className="text-2xl font-bold text-teal-600">{impactData.roles_affected}</div>
              <div className="text-xs text-gray-600">Roles Affected</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
              <div className="text-2xl font-bold text-cyan-600">{impactData.departments_affected.length || 1}</div>
              <div className="text-xs text-gray-600">Departments</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
              <div className="text-2xl font-bold text-blue-600">{impactData.estimated_training_hours}h</div>
              <div className="text-xs text-gray-600">Training Hours</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
              <div className={`text-2xl font-bold ${getVelocityColor(impactData.change_velocity)}`}>
                {impactData.change_velocity.toUpperCase()}
              </div>
              <div className="text-xs text-gray-600">Change Velocity</div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <GraduationCap className="w-4 h-4" />
              Training Requirements
            </h4>
            <div className="space-y-2">
              {impactData.training_requirements.map((training, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      training.training_type === 'advanced' ? 'bg-purple-100 text-purple-700' :
                      training.training_type === 'intermediate' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {training.training_type.toUpperCase()}
                    </span>
                    <span className="font-medium text-gray-800">{training.role}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {training.estimated_hours}h
                    </span>
                    <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                      {training.delivery_method.replace('-', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <Workflow className="w-4 h-4" />
                Rollout Strategy
              </h4>
              <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
                <span className="text-3xl">{getRolloutIcon(impactData.rollout_recommendation)}</span>
                <div>
                  <div className="font-bold text-teal-800">{getRolloutLabel(impactData.rollout_recommendation)}</div>
                  <div className="text-xs text-teal-600">
                    {impactData.rollout_recommendation === 'phased' && 'Deploy in stages to minimize risk'}
                    {impactData.rollout_recommendation === 'pilot' && 'Test with small group before full rollout'}
                    {impactData.rollout_recommendation === 'big_bang' && 'Deploy to all users simultaneously'}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4" />
                Support Requirements
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Tier 1 Volume Increase</span>
                  <span className="font-bold text-orange-600">{impactData.support_requirements.tier1_volume_increase}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Documentation Updates</span>
                  <span className="font-bold text-blue-600">{impactData.support_requirements.documentation_updates}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">FAQ Items Needed</span>
                  <span className="font-bold text-purple-600">{impactData.support_requirements.faq_items_needed}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4" />
              Communication Plan
            </h4>
            <div className="space-y-2">
              {impactData.communication_triggers.map((trigger, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
                  <span className="flex items-center justify-center w-6 h-6 bg-teal-100 text-teal-700 rounded-full text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-sm text-gray-700">{trigger}</span>
                </div>
              ))}
            </div>
          </div>

          {impactData.departments_affected.length > 0 && (
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4" />
                Departments Impacted
              </h4>
              <div className="flex flex-wrap gap-2">
                {impactData.departments_affected.map((dept, idx) => (
                  <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {dept}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChangeImpactAssessment;
